import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import BackofficeReportsApi from './backofficeReportsApi';

const MAX_EXPORT_DAYS = 45;
const BUSINESS_TIMEZONE = 'Europe/Bucharest';

function localIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function businessToday() {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: BUSINESS_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const values = parts.reduce((result, part) => ({
      ...result,
      [part.type]: part.value,
    }), {});
    return `${values.year}-${values.month}-${values.day}`;
  } catch (error) {
    return localIsoDate(new Date());
  }
}

function shiftIsoDate(value, days) {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().substring(0, 10);
}

function inclusiveDays(dateFrom, dateTo) {
  const start = Date.parse(`${dateFrom}T00:00:00Z`);
  const end = Date.parse(`${dateTo}T00:00:00Z`);

  if (Number.isNaN(start) || Number.isNaN(end)) return 0;

  return Math.round((end - start) / 86400000) + 1;
}

function rangeForPreset(preset, selectedAnchorDate) {
  const businessDate = businessToday();
  const anchorDate = selectedAnchorDate || (
    preset === 'yesterday' || preset === 'last_7d' || preset === 'last_30d'
      ? shiftIsoDate(businessDate, -1)
      : businessDate
  );
  if (preset === 'today' || preset === 'yesterday') {
    return { dateFrom: anchorDate, dateTo: anchorDate };
  }
  if (preset === 'this_month') {
    return {
      dateFrom: `${anchorDate.substring(0, 7)}-01`,
      dateTo: anchorDate,
    };
  }

  if (preset === 'last_month') {
    const [year, month] = anchorDate.split('-').map(Number);
    const lastDay = new Date(Date.UTC(year, month - 1, 0));
    const firstDay = new Date(Date.UTC(
      lastDay.getUTCFullYear(),
      lastDay.getUTCMonth(),
      1,
    ));
    return {
      dateFrom: firstDay.toISOString().substring(0, 10),
      dateTo: lastDay.toISOString().substring(0, 10),
    };
  }

  const days = preset === 'last_7d' ? 7 : 30;
  return {
    dateFrom: shiftIsoDate(anchorDate, -(days - 1)),
    dateTo: anchorDate,
  };
}

function dashboardRange(periodMode, selectedDateFrom, selectedDateTo) {
  if (periodMode === 'custom' && selectedDateFrom && selectedDateTo) {
    return { dateFrom: selectedDateFrom, dateTo: selectedDateTo, rangePreset: 'custom' };
  }
  if (periodMode === 'today' || periodMode === 'yesterday') {
    const fallbackDate = periodMode === 'today'
      ? businessToday()
      : shiftIsoDate(businessToday(), -1);
    const dateTo = selectedDateTo || fallbackDate;
    return {
      dateFrom: selectedDateFrom || dateTo,
      dateTo,
      rangePreset: periodMode,
    };
  }

  const rangePreset = periodMode === 'last_7d' ? 'last_7d' : 'last_30d';
  const selectedDays = inclusiveDays(selectedDateFrom, selectedDateTo);
  const expectedDays = rangePreset === 'last_7d' ? 7 : 30;
  const range = selectedDays === expectedDays
    ? { dateFrom: selectedDateFrom, dateTo: selectedDateTo }
    : rangeForPreset(rangePreset, selectedDateTo || undefined);
  return { ...range, rangePreset };
}

function csvValue(value) {
  if (value === null || typeof value === 'undefined') return '""';
  const stringValue = String(value);
  const trimmedValue = stringValue.trimStart();
  const numericValue = /^-?\d+(?:\.\d+)?%?$/.test(trimmedValue.trim());
  const safeValue = !numericValue && /^[=+\-@\t\r]/.test(trimmedValue) ? `'${stringValue}` : stringValue;
  return `"${safeValue.replace(/"/g, '""')}"`;
}

function requestErrorMessage(error) {
  const status = Number(error && error.status);
  const response = error && error.responseJSON;

  if (error && error.code === 'client_county_filter_not_applied') {
    return 'The backend did not apply the selected client county. No file was downloaded.';
  }
  if (status === 401) return 'Your session has expired. Sign in again and retry.';
  if (status === 403) return 'You do not have permission to export this report.';
  if (status === 404) return 'The detailed promotions export is not available on this backend.';
  if (status === 400) {
    const dateError = response && (response.dateTo || response.dateFrom || response.report);
    return dateError ? String(dateError) : 'The export dates are not valid.';
  }
  if (status >= 500) return 'The server could not prepare the export. Please retry.';
  if (error && error.statusText === 'abort') return 'The export request was cancelled.';
  return 'The export could not be loaded. Check the connection and retry.';
}

class PromotionsDetailedExport extends React.Component {
  constructor(props) {
    super(props);

    const range = dashboardRange(
      props.periodMode,
      props.dateFrom,
      props.dateTo,
    );

    this.state = {
      open: false,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      rangePreset: range.rangePreset,
      loadingReport: '',
      error: '',
      notice: '',
      success: '',
    };
    this.requestId = 0;
  }

  componentWillUnmount() {
    this.requestId += 1;
  }

  handleOpen = () => {
    const range = dashboardRange(
      this.props.periodMode,
      this.props.dateFrom,
      this.props.dateTo,
    );

    this.setState({
      open: true,
      ...range,
      error: '',
      notice: '',
      success: '',
    });
  };

  handleClose = () => {
    if (this.state.loadingReport) return;
    this.setState({ open: false, error: '', notice: '', success: '' });
  };

  handleDateChange = (field) => (event) => {
    const value = event.target.value;
    this.setState((previousState) => {
      const dates = { [field]: value };

      if (field === 'dateFrom' && value && previousState.dateTo && value > previousState.dateTo) {
        dates.dateTo = value;
      }
      if (field === 'dateTo' && value && previousState.dateFrom && value < previousState.dateFrom) {
        dates.dateFrom = value;
      }

      return {
        ...dates,
        rangePreset: 'custom',
        error: '',
        notice: '',
        success: '',
      };
    });
  };

  handlePresetChange = (event, rangePreset) => {
    if (!rangePreset) return;
    if (rangePreset === 'custom') {
      this.setState({
        rangePreset,
        error: '',
        notice: '',
        success: '',
      });
      return;
    }

    this.setState({
      ...rangeForPreset(rangePreset),
      rangePreset,
      error: '',
      notice: '',
      success: '',
    });
  };

  validateDates = () => {
    const { dateFrom, dateTo } = this.state;
    const days = inclusiveDays(dateFrom, dateTo);

    if (!dateFrom || !dateTo || days < 1) {
      return 'Select a valid start and end date.';
    }
    if (days > MAX_EXPORT_DAYS) {
      return `Choose no more than ${MAX_EXPORT_DAYS} inclusive days.`;
    }
    if (dateTo > businessToday()) {
      return 'The end date cannot be in the future.';
    }
    return '';
  };

  buildCsv = (report, response) => {
    const meta = response.meta || {};
    const summary = response.summary || {};
    const rows = Array.isArray(response.rows) ? response.rows : [];
    const currency = meta.currency || 'RON';
    const common = [
      ['Report', report],
      ['Date from', meta.dateFrom || this.state.dateFrom],
      ['Date to', meta.dateTo || this.state.dateTo],
      ['Timezone', meta.timezone || ''],
      ['Currency', currency],
      ['Generated at', new Date().toLocaleString()],
      [
        'Client county filter',
        meta.clientCountyFilter?.label || this.props.countyName || 'All counties',
      ],
      ['Geography basis', meta.geographyBasis || ''],
      ['Unknown geography', meta.unknownGeographyBucket || 'Other / unknown'],
      '',
    ];

    if (report === 'claims_by_county') {
      const headers = [
        ['date', 'Date'],
        ['promotion_name', 'Promotion name'],
        ['county_iref', 'County ID'],
        ['county_name', 'Current client county'],
        ['claims_issued', 'Claims issued'],
        ['issued_claims_consumed_by_period_end', 'Consumed by period end'],
        ['issued_claims_unconsumed_at_period_end', 'Unconsumed at period end'],
        ['issued_claim_conversion', 'Issued conversion %'],
      ];
      return [
        ...common,
        ['Summary'],
        ['Claims issued', summary.claims_issued || 0],
        ['Consumed by period end', summary.issued_claims_consumed_by_period_end || 0],
        ['Unconsumed at period end', summary.issued_claims_unconsumed_at_period_end || 0],
        ['Issued conversion %', Number(summary.issued_claim_conversion || 0).toFixed(2)],
        '',
        headers.map((header) => header[1]),
        ...rows.map((row) => headers.map((header) => row[header[0]])),
      ];
    }

    const headers = [
      ['date', 'Date'],
      ['promotion_name', 'Promotion name'],
      ['location_iref', 'Location ID'],
      ['location_name', 'Consumption location'],
      ['redeemed_claims', 'Redeemed claims'],
      ['promo_orders', 'Promo orders'],
      ['promo_order_value', `Net promo sales (${currency})`],
      ['promotion_discount', `Promotion discount (${currency})`],
      ['average_discount_per_promo_order', `Average discount/order (${currency})`],
      ['discount_rate', 'Discount rate (% of gross promo sales)'],
    ];
    return [
      ...common,
      ['Summary'],
      ['Redeemed claims', summary.redeemed_claims || 0],
      ['Promo orders', summary.promo_orders || 0],
      [`Net promo sales (${currency})`, Number(summary.promo_order_value || 0).toFixed(2)],
      [`Promotion discount (${currency})`, Number(summary.promotion_discount || 0).toFixed(2)],
      [`Average discount/order (${currency})`, Number(summary.average_discount_per_promo_order || 0).toFixed(2)],
      ['Discount rate (% of gross promo sales)', Number(summary.discount_rate || 0).toFixed(2)],
      '',
      headers.map((header) => header[1]),
      ...rows.map((row) => headers.map((header) => row[header[0]])),
    ];
  };

  downloadCsv = (report, response) => {
    const csvRows = this.buildCsv(report, response);
    const content = csvRows
      .map((row) => Array.isArray(row) ? row.map(csvValue).join(',') : '')
      .join('\n');
    const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `promotions_${report}_${this.state.dateFrom}_${this.state.dateTo}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  handleExport = (report) => {
    const validationError = this.validateDates();
    if (validationError) {
      this.setState({ error: validationError, notice: '', success: '' });
      return;
    }

    const requestId = ++this.requestId;
    this.setState({ loadingReport: report, error: '', notice: '', success: '' });

    BackofficeReportsApi.getPromotionsDetailedExportAsync(this.props.client, {
      report,
      dateFrom: this.state.dateFrom,
      dateTo: this.state.dateTo,
      county: this.props.county || 'all',
    }).then((response) => {
      if (requestId !== this.requestId) return;
      if (
        (this.props.county || 'all') !== 'all'
        && String(response?.meta?.clientCounty || '') !== String(this.props.county)
      ) {
        const unsupportedFilterError = new Error('Client county filter was not applied.');
        unsupportedFilterError.code = 'client_county_filter_not_applied';
        throw unsupportedFilterError;
      }
      if (!response || !Array.isArray(response.rows) || response.rows.length === 0) {
        this.setState({
          loadingReport: '',
          notice: 'No matching promotion activity was found. No file was downloaded.',
        });
        return;
      }
      this.downloadCsv(report, response || {});
      this.setState({
        loadingReport: '',
        success: 'CSV downloaded. It contains aggregate rows only.',
      });
    }).catch((error) => {
      if (requestId !== this.requestId) return;
      this.setState({
        loadingReport: '',
        error: requestErrorMessage(error),
      });
    });
  };

  render() {
    const {
      open,
      dateFrom,
      dateTo,
      rangePreset,
      loadingReport,
      error,
      notice,
      success,
    } = this.state;
    const days = inclusiveDays(dateFrom, dateTo);
    const busy = Boolean(loadingReport);
    const today = businessToday();

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadRoundedIcon />}
          onClick={this.handleOpen}
          sx={{
            flexShrink: 0,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 800,
            whiteSpace: 'nowrap',
          }}
        >
          Detailed CSV
        </Button>

        <Dialog open={open} onClose={this.handleClose} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 800 }}>Detailed promotions CSV</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Typography color="text.secondary" variant="body2">
                These dates apply only to this export. Maximum {MAX_EXPORT_DAYS} inclusive days;
                client identity is not included.
              </Typography>

              <Alert severity="info" icon={false}>
                Client county: {this.props.countyName || 'All counties'}
              </Alert>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={rangePreset}
                onChange={this.handlePresetChange}
                aria-label="Export period presets"
                disabled={busy}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  '& .MuiToggleButtonGroup-grouped': {
                    m: 0,
                    px: 1.25,
                    py: 0.5,
                    border: '1px solid rgba(15, 23, 42, 0.14) !important',
                    borderRadius: '9px !important',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    textTransform: 'none',
                  },
                }}
              >
                <ToggleButton value="today" title="Today (in progress)">Today</ToggleButton>
                <ToggleButton value="yesterday" aria-label="Yesterday" title="Yesterday">YDay</ToggleButton>
                <ToggleButton value="last_7d">Last 7 complete days</ToggleButton>
                <ToggleButton value="last_30d">Last 30 complete days</ToggleButton>
                <ToggleButton value="this_month">This month</ToggleButton>
                <ToggleButton value="last_month">Last month</ToggleButton>
                <ToggleButton value="custom">Custom</ToggleButton>
              </ToggleButtonGroup>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date from"
                  value={dateFrom}
                  onChange={this.handleDateChange('dateFrom')}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: rangePreset !== 'custom' }}
                  inputProps={{ max: today }}
                  disabled={busy}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="Date to"
                  value={dateTo}
                  onChange={this.handleDateChange('dateTo')}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: rangePreset !== 'custom' }}
                  inputProps={{ max: today }}
                  disabled={busy}
                />
              </Stack>

              <Typography variant="caption" color={days > MAX_EXPORT_DAYS ? 'error' : 'text.secondary'}>
                {days > 0 ? `${days} inclusive day${days === 1 ? '' : 's'} selected` : 'Select both dates'}
                {rangePreset !== 'custom' ? ' · Select Custom to edit dates.' : ''}
              </Typography>

              {error && <Alert severity="error">{error}</Alert>}
              {notice && <Alert severity="info">{notice}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  bgcolor: '#f8fafc',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Claims by current client county
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  One row per issue date and current county, including period-end conversion.
                </Typography>
                <Button
                  fullWidth
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 800 }}
                  variant="contained"
                  disabled={busy}
                  onClick={() => this.handleExport('claims_by_county')}
                  startIcon={loadingReport === 'claims_by_county' ? <CircularProgress size={16} color="inherit" /> : <DownloadRoundedIcon />}
                >
                  Export claims by county
                </Button>
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  bgcolor: '#f8fafc',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Redemptions by consumption location
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  One row per order date and location, with order value and promotion discount.
                </Typography>
                <Button
                  fullWidth
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 800 }}
                  variant="contained"
                  disabled={busy}
                  onClick={() => this.handleExport('redemptions_by_location')}
                  startIcon={loadingReport === 'redemptions_by_location' ? <CircularProgress size={16} color="inherit" /> : <DownloadRoundedIcon />}
                >
                  Export redemptions by location
                </Button>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleClose} disabled={busy} sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

export default PromotionsDetailedExport;
